import type { Icon } from '@phosphor-icons/react';
import {
  ArrowClockwise,
  BookOpen,
  CaretLeft,
  CaretRight,
  CornersOut,
  MagnifyingGlassMinus,
  MagnifyingGlassPlus,
  Sidebar,
} from '@phosphor-icons/react';
import {
  formatPercent,
  type MessageKey,
  nextZoomStop,
  type Translator,
  ZOOM_MAX,
  ZOOM_MIN,
} from 'pdf-shared';
import { Tooltip } from 'pdf-ui/ui';
import type { ZoomRequest } from 'pdf-ui/zoom';
import { lazy, Suspense, useId, useState } from 'react';
import { toggleLeftDock, useCore } from '../features/core/core-store';
import { toggleReading, useReading } from '../features/reading/reading-store';

/**
 * Page and view controls, **in the status bar**.
 *
 * They sit in the bar rather than floating in the viewer's bottom-right corner: over the
 * page itself they would cover text at every width, and below 1024 px a third of a line.
 * In the bar that already reports page and zoom they cover nothing, and the bar keeps one row.
 *
 * "Rotate" turns the **current page** unless pages are selected in the page panel, in
 * which case it turns those — the same rule every page action follows (`runPageAction`).
 *
 * The two view toggles (the page dock, reading mode) read and write their stores directly: the
 * bar shows them pressed from the state, and the shell carries neither down to it.
 */
export interface PageNavigationProps {
  readonly t: Translator;
  readonly currentPage: number;
  readonly pageCount: number;
  readonly onGoToPage: (pageIndex: number) => void;
  readonly zoom: number;
  readonly onZoomChange: (zoom: ZoomRequest) => void;
  /** Absent while the document cannot be edited. */
  readonly onRotate?: () => void;
  readonly onToggleFullscreen: () => void;
}

/**
 * The zoom menu is a popover, and its floating-UI chunk would put the first paint over the
 * JavaScript budget: it loads when the bar first shows, and the percentage is drawn as plain text
 * until it has.
 */
const ZoomMenu = lazy(async () => {
  const module = await import('pdf-ui/zoom');
  return { default: module.ZoomMenu };
});

const ICON_BUTTON =
  'flex size-6 shrink-0 items-center justify-center rounded text-kumo-default transition-colors hover:bg-kumo-recessed hover:text-kumo-strong focus-visible:outline-2 focus-visible:outline-kumo-focus disabled:opacity-30';
const PRESSED = 'bg-pdf-accent text-pdf-on-accent hover:bg-pdf-accent hover:text-pdf-on-accent';

function IconButton({
  label,
  hint,
  icon: Glyph,
  onClick,
  disabled,
  pressed,
  mirrored = false,
}: {
  readonly label: string;
  /** What the button does, in a sentence: the tooltip and the accessible description. */
  readonly hint?: string;
  readonly icon: Icon;
  /** A direction glyph (previous/next) that turns over in a right-to-left UI. */
  readonly mirrored?: boolean;
  readonly onClick: () => void;
  readonly disabled?: boolean;
  readonly pressed?: boolean;
}) {
  return (
    <Tooltip label={hint ?? label} side="top">
      <button
        type="button"
        aria-label={label}
        aria-description={hint}
        {...(pressed === undefined ? {} : { 'aria-pressed': pressed })}
        disabled={disabled}
        onClick={onClick}
        className={`${ICON_BUTTON} ${pressed === true ? PRESSED : ''}`}
      >
        <Glyph size={14} className={mirrored ? 'rtl:-scale-x-100' : undefined} aria-hidden="true" />
      </button>
    </Tooltip>
  );
}

export function PageNavigation({
  t,
  currentPage,
  pageCount,
  onGoToPage,
  zoom,
  onZoomChange,
  onRotate,
  onToggleFullscreen,
}: PageNavigationProps) {
  const [pageInput, setPageInput] = useState<string | null>(null);
  const [pageInvalid, setPageInvalid] = useState(false);
  const pageMessageId = useId();
  const thumbnailsOpen = useCore((state) => state.leftDock);
  const readingActive = useReading((state) => state.reading);
  const label = (key: MessageKey) => t(key);

  return (
    <fieldset aria-label={t('nav.controls')} className="m-0 flex shrink-0 items-center gap-0.5 border-0 p-0">
      <IconButton
        label={label('nav.prevPage')}
        icon={CaretLeft}
        mirrored
        disabled={currentPage <= 0}
        onClick={() => onGoToPage(currentPage - 1)}
      />
      <form
        className="flex items-center gap-1"
        onSubmit={(event) => {
          event.preventDefault();
          // Nothing typed: nothing to check.
          if (pageInput === null) return;
          const typed = pageInput.trim();
          const page = /^\d+$/.test(typed) ? Number(typed) : Number.NaN;
          if (page >= 1 && page <= pageCount) {
            onGoToPage(page - 1);
            setPageInput(null);
          } else {
            // Say so and keep what was typed, so the mistake is in front of the reader.
            setPageInvalid(true);
          }
        }}
      >
        <input
          type="text"
          inputMode="numeric"
          aria-label={label('nav.pageNumber')}
          value={pageInput ?? String(currentPage + 1)}
          aria-invalid={pageInvalid}
          aria-describedby={pageInvalid ? pageMessageId : undefined}
          onChange={(event) => {
            setPageInput(event.target.value);
            setPageInvalid(false);
          }}
          onFocus={(event) => {
            setPageInput(String(currentPage + 1));
            event.currentTarget.select();
          }}
          onBlur={() => {
            setPageInput(null);
            setPageInvalid(false);
          }}
          className="h-5 w-8 rounded-sm border border-kumo-line bg-kumo-base px-0.5 text-center text-xs tabular-nums text-kumo-strong aria-invalid:border-kumo-danger focus:border-pdf-accent focus:outline-hidden"
        />
        <span className="tabular-nums text-kumo-subtle">/ {pageCount}</span>
        {pageInvalid ? (
          <span id={pageMessageId} role="alert" className="whitespace-nowrap text-kumo-danger">
            {t('nav.pageInvalid', { count: pageCount })}
          </span>
        ) : null}
      </form>
      <IconButton
        label={label('nav.nextPage')}
        icon={CaretRight}
        mirrored
        disabled={currentPage >= pageCount - 1}
        onClick={() => onGoToPage(currentPage + 1)}
      />

      <span aria-hidden="true" className="mx-1 h-4 w-px bg-kumo-line" />

      <IconButton
        label={label('nav.zoomOut')}
        icon={MagnifyingGlassMinus}
        disabled={zoom <= ZOOM_MIN}
        onClick={() => onZoomChange(nextZoomStop(zoom, 'out'))}
      />
      <Suspense
        fallback={
          <span className="min-w-11 px-1 text-center text-xs font-semibold tabular-nums text-kumo-subtle">
            {formatPercent(zoom, t.locale)}
          </span>
        }
      >
        <ZoomMenu t={t} zoom={zoom} onZoomChange={onZoomChange} />
      </Suspense>
      <IconButton
        label={label('nav.zoomIn')}
        icon={MagnifyingGlassPlus}
        disabled={zoom >= ZOOM_MAX}
        onClick={() => onZoomChange(nextZoomStop(zoom, 'in'))}
      />

      <span aria-hidden="true" className="mx-1 h-4 w-px bg-kumo-line" />

      {onRotate === undefined ? null : (
        <IconButton
          label={label('nav.rotate')}
          hint={label('nav.rotate.hint')}
          icon={ArrowClockwise}
          onClick={onRotate}
        />
      )}
      <IconButton
        label={label('nav.togglePages')}
        icon={Sidebar}
        pressed={thumbnailsOpen}
        onClick={toggleLeftDock}
      />
      <IconButton
        label={label('nav.readingMode')}
        icon={BookOpen}
        pressed={readingActive}
        onClick={toggleReading}
      />
      <IconButton label={label('nav.fullscreen')} icon={CornersOut} onClick={onToggleFullscreen} />
    </fieldset>
  );
}
